import api from '../api/apiClient';
import { resolveAgentName, withCanonicalAgentName } from '../../utils/agentName';

const OPS = '/ops/api/v1';
export const CONVERSATION_REQUEST_TIMEOUT_MS = 30000;

export const conversationExplorerService = {
  async getTraces(params) {
    try {
      // Map parameters for ops-service
      const serviceParams = {
        ...params,
        page_size: params.per_page || 10, // Backend expects page_size
        organization_id: params.organization_id
      };

      console.log('[conversationExplorerService] Calling GET /log-tracer/traces with params:', serviceParams);

      const response = await api.get(`${OPS}/log-tracer/traces`, {
        params: serviceParams,
        timeout: CONVERSATION_REQUEST_TIMEOUT_MS,
      });

      console.log('[conversationExplorerService] Response status:', response.status);

      // Normalize response from Backend (log_tracer.py)
      // Backend returns: { conversations: [], summary: { total_conversations: ... }, pagination: ... }
      // Frontend expects: { traces: [], total: ... }

      const responseData = response.data;

      const normalizedData = {
        traces: (responseData.conversations || []).map((trace) => withCanonicalAgentName(trace)),
        total: responseData.summary?.total_conversations || responseData.pagination?.total_items || 0,
        total_spans: responseData.summary?.total_spans || 0,
        conversations_with_threats: responseData.summary?.total_threats || 0,
        page: responseData.pagination?.page || params.page || 1,
        total_pages: responseData.pagination?.total_pages || 1,
        per_page: responseData.pagination?.page_size || params.per_page || 10
      };

      console.log('[conversationExplorerService] Normalized Response:', {
        tracesCount: normalizedData.traces.length,
        total: normalizedData.total
      });

      return normalizedData;
    } catch (error) {
      console.error('[conversationExplorerService] Error in getTraces:', error);

      // Enhanced error logging
      if (error.response) {
        console.error('[conversationExplorerService] Response status:', error.response.status);
        console.error('[conversationExplorerService] Response data:', error.response.data);
      } else if (error.request) {
        console.error('[conversationExplorerService] No response received from Ops Service');
      }

      throw error;
    }
  },

  async getFilterOptions(params) {
    try {
      console.log('[conversationExplorerService] Calling GET /log-tracer/filters with params:', params);
      const response = await api.get(`${OPS}/log-tracer/filters`, { params });
      return {
        ...response.data,
        agents: (response.data?.agents || []).map((agent) => ({
          ...agent,
          label: resolveAgentName({ agent_identifier: agent.value, agent_name: agent.label }),
        })),
      };
    } catch (error) {
      console.error('[conversationExplorerService] Error in getFilterOptions:', error);
      return { agents: [], models: [], severities: [], threat_types: [], users: [], data_modes: [] };
    }
  },

  async getConversationDetails(conversationId, params) {
    try {
      console.log('[conversationExplorerService] Calling GET /log-tracer/conversations/' + conversationId);

      const response = await api.get(`${OPS}/log-tracer/conversations/${conversationId}`, { params });

      const data = response.data || {};
      return {
        ...data,
        ...withCanonicalAgentName(data),
        summary: data.summary ? withCanonicalAgentName(data.summary) : data.summary,
        spans: (data.spans || []).map((span) => withCanonicalAgentName(span)),
      };
    } catch (error) {
      console.error('[conversationExplorerService] Error in getConversationDetails:', error);
      throw error;
    }
  },

  async getStats(params) {
    try {
      // Map parameters
      const serviceParams = {
        ...params,
        organization_id: params.organization_id
      };

      console.log('[conversationExplorerService] Calling GET /log-tracer/stats with params:', serviceParams);

      const response = await api.get(`${OPS}/log-tracer/stats`, {
        params: serviceParams,
        timeout: CONVERSATION_REQUEST_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      console.error('[conversationExplorerService] Error in getStats:', error);
      return {};
    }
  },

  // AI-driven conversation drift analysis for a Log Tracer conversation.
  // POSTs the conversation context (user prompt + AI chunked responses,
  // pulled from the trace spans) to the shared AI microservice endpoint,
  // which detects drift / hallucination / risk signals and caches the
  // result by session_id. Powers the "Saf3AI Conversation Analysis" card
  // in the timeline panel — same analysis surfaced for Copilot / Gemini.
  async getConversationDriftAnalysis({ session_id, user_prompt, ai_responses, application, user_email, content_status, force_regenerate = false }) {
    try {
      const params = force_regenerate ? { force_regenerate: 'true' } : {};
      const response = await api.post(
        '/ai-explorer/api/v1/agent/copilot-conversation-drift',
        { session_id, user_prompt, ai_responses, application, user_email, content_status },
        { params },
      );
      return response.data;
    } catch (err) {
      console.error('[conversationExplorerService] Error in getConversationDriftAnalysis:', err);
      return { ai_summary: null, error: err?.response?.data?.error || err?.message || 'Failed to load drift analysis' };
    }
  },

  async getModelPricing() {
    try {
      // Get org ID from local storage user object as fallback/primary
      let orgId = 1; // Default
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          orgId = user.organization_id || user.org_id || user.organizationId || 1;
        }
      } catch (e) {
        console.warn('Error reading user from localStorage', e);
      }

      console.log(`[conversationExplorerService] Calling GET /observability/model-pricing with orgId=${orgId}`);

      // Pass org ID explicitly to avoid interceptor issues or to be safe
      const response = await api.get(`${OPS}/observability/model-pricing`, {
        params: { organization_id: orgId }
      });
      return response.data;
    } catch (error) {
      console.error('[conversationExplorerService] Error in getModelPricing:', error);
      // Fallback to empty list (not object, as improved hook expects list or handles list)
      return [];
    }
  },
};
